import { useAuth } from "@clerk/clerk-react";
import { Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { ReactNode, useContext } from "react";
import { AddOnItemName } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";

interface AccountReviewState {
  isInCustomerSupportVariant: boolean;
  hasPurchasedAccountReview: boolean;
  canSubmitReview: boolean;
}

export const useAccountReviewState = (): AccountReviewState => {
  const { addOns, subscription } = useContext(SubscriptionContext);

  const customerSupportAddOn = addOns?.[AddOnItemName.customer_support];
  const isInCustomerSupportVariant = Boolean(customerSupportAddOn);
  const hasPurchasedAccountReview = Boolean(subscription?.customerSupportData);
  const canSubmitReview =
    isInCustomerSupportVariant && hasPurchasedAccountReview;

  return {
    isInCustomerSupportVariant,
    hasPurchasedAccountReview,
    canSubmitReview,
  };
};

interface CompleteScreenOptions {
  defaultTitle: string | JSX.Element;
  defaultDescription: ReactNode;
  defaultButtonNext: string;
  defaultStepLabel: string;
  defaultBody?: JSX.Element;
  handleNext: () => void;
  onSkip?: () => void;
}

interface CompleteScreenOverrides {
  title: string | JSX.Element;
  description: JSX.Element;
  body: JSX.Element;
  buttonNext: string;
  onNext: () => void;
  onFinishSetUpLater?: () => void;
  finishSetUpLaterLabel?: string;
  stepLabel: string;
}

export const useAccountReviewCompleteScreen = ({
  defaultTitle,
  defaultDescription,
  defaultButtonNext,
  defaultStepLabel,
  defaultBody,
  handleNext,
  onSkip,
}: CompleteScreenOptions): CompleteScreenOverrides => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { canSubmitReview } = useAccountReviewState();

  const title = canSubmitReview
    ? "One last step — submit your account for review 💎"
    : defaultTitle;

  const description = canSubmitReview ? (
    <Typography sx={{ fontSize: { xs: 13, md: 18 }, my: 3 }}>
      <strong>Boost your reply rate</strong> before you start pitching! Have our
      Customer Success team review your templates, follow-up sequences, and
      profile so you're set up for success.
    </Typography>
  ) : (
    <>{defaultDescription}</>
  );

  const submitReviewAndComplete = async () => {
    if (!currentOrg?.id) return;
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/onboarding-review-requests`,
        "POST",
      );
    } catch (error) {
      setErrorAlert(error);
      return;
    }
    handleNext();
  };

  return {
    title,
    description,
    body: defaultBody || <></>,
    buttonNext: canSubmitReview ? "Review My Account" : defaultButtonNext,
    onNext: canSubmitReview ? submitReviewAndComplete : handleNext,
    onFinishSetUpLater: canSubmitReview ? onSkip : undefined,
    finishSetUpLaterLabel: canSubmitReview ? "Skip" : undefined,
    stepLabel: canSubmitReview ? "Review Account" : defaultStepLabel,
  };
};
